import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogFooter, DialogTrigger } from 'shared/components/ui/dialog';
import { Input } from 'shared/components/ui/input';
import { Label } from 'shared/components/ui/label';
import { FinecoButton } from 'pages/assistant/components/FinecoButton';
import { Column, DataSheetGrid, keyColumn, textColumn } from 'react-datasheet-grid';
import { useToast } from 'shared/components/ui/use-toast';
import { ConstraintsService } from 'shared/services/ConstraintsService';
import colorsStore from 'shared/store/colorsStore';
import 'react-datasheet-grid/dist/style.css';
import '../../../../chat/components/excelPast.css';

import styles from './AddProductListModal.module.scss';

type Row = {
  isin: string | null;
};

type AddProductListModalProps = {
  onSuccess: () => void;
  children: React.ReactNode;
};

export const AddProductListModal: React.FC<AddProductListModalProps> = ({ onSuccess, children }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const [open, setOpen] = useState(false);
  const [listName, setListName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rowsToAdd, setRowsToAdd] = useState(1);
  const [data, setData] = useState<Row[]>([{ isin: '' }]);

  const baseColumns: Column<Row>[] = [
    {
      ...keyColumn<Row, 'isin'>('isin', textColumn),
      title: 'ISIN',
    },
  ];

  const deleteRow = (rowIndex: number) => {
    setData((prevData) => {
      const newData = [...prevData];
      newData.splice(rowIndex, 1);

      // If we've deleted all rows, add an empty one
      if (newData.length === 0) {
        return [{ isin: '' }];
      }

      return newData;
    });
  };

  const dataLengthRef = useRef(-1);
  useEffect(() => {
    dataLengthRef.current = data.length;
  }, [data]);

  const deleteColumn: Column<Row> = {
    title: '',
    width: 30,
    minWidth: 30,
    maxWidth: 30,
    component: ({ rowIndex }) => {
      if (dataLengthRef.current === 1) return null;
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              deleteRow(rowIndex);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '4px',
              color: colorsConfig.finecoBlack,
              opacity: 1,
              transition: 'opacity 0.2s',
              outline: 'none',
              boxShadow: 'none',
              marginBottom: 10,
              width: 16,
              height: 16,
              WebkitTapHighlightColor: 'transparent',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = '0.7';
            }}
          >
            <svg
              style={{ width: '16px', height: '16px', flexShrink: 0 }}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#00549F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      );
    },
    disabled: true,
  };

  const columns = useMemo(() => {
    return data.length === 1 ? baseColumns : [...baseColumns, deleteColumn];
  }, [data.length]);

  const handleRowChange = (newData: Row[]) => {
    // Check if any cell contains multiple ISINs (pasted with spaces/tabs/newlines)
    let hasMultipleInOneCell = false;
    for (const row of newData) {
      if (row.isin && row.isin.includes(' ')) {
        hasMultipleInOneCell = true;
        break;
      }
    }

    if (hasMultipleInOneCell) {
      // Expand multi-value cells into separate rows
      const expandedData: Row[] = [];
      for (const row of newData) {
        if (row.isin) {
          const parts = row.isin.split(/\s+/).map(s => s.trim()).filter(Boolean);
          for (const part of parts) {
            expandedData.push({ isin: part.toUpperCase() });
          }
        } else {
          expandedData.push({ isin: null });
        }
      }
      setData(expandedData.length > 0 ? expandedData : [{ isin: '' }]);
    } else {
      // Normal change - just uppercase
      const uppercaseData = newData.map((row) => ({
        ...row,
        isin: row.isin?.toUpperCase() ?? null,
      }));
      setData(uppercaseData);
    }
  };

  const handleSubmit = async () => {
    if (!listName.trim()) {
      toast({
        title: t('global.error'),
        description: t('instrumentLists.nameRequired'),
        variant: 'destructive',
      });
      return;
    }

    const isins = data.map((row) => row.isin).filter((isin) => isin && isin.trim() !== '') as string[];

    if (isins.length === 0) {
      toast({
        title: t('global.error'),
        description: t('instrumentLists.atLeastOneIsin'),
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      await ConstraintsService.createProductList({
        name: listName.trim(),
        isins,
      });

      toast({
        title: t('global.success'),
        description: t('instrumentLists.addListSuccess'),
      });

      // Reset form
      setListName('');
      setData([{ isin: '' }]);
      setOpen(false);

      // Refetch product lists
      onSuccess();
    } catch (error: any) {
      const detail =
        error?.data?.detail ??
        error?.message ??
        t('global.errorOccurred');
      toast({
        title: t('global.error'),
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitDisabled =
    submitting ||
    !listName.trim() ||
    data.length === 0 ||
    data.every((row) => !row.isin || !row.isin.trim());

  useEffect(() => {
    if (!open) return;

    let grid: Element | null = null;

    const handleCellClick = (e: any) => {
      const cell = e.target.closest('.dsg-cell');
      if (cell) {
        const input = cell.querySelector('input');
        if (input) {
          setTimeout(() => {
            input.focus();
            input.select();
          }, 0);
        }
      }
    };

    // Delay to ensure DOM is mounted after dialog opens
    const timeoutId = setTimeout(() => {
      grid = document.querySelector('.custom-datasheet-wrapper');
      if (grid) {
        grid.addEventListener('click', handleCellClick);
      }
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (grid) {
        grid.removeEventListener('click', handleCellClick);
      }
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent title={t('instrumentLists.addList')} className={styles.dialogContent}>
        <div className={styles.modalBody}>
          <div className={styles.nameInputContainer}>
            <Label htmlFor="listName" style={{ color: colorsConfig.finecoBlack }}>
              {t('instrumentLists.listName')}
            </Label>
            <Input
              id="listName"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder={t('instrumentLists.enterListName')}
              disabled={submitting}
            />
          </div>

          <div className={styles.gridContainer}>
            <Label style={{ color: colorsConfig.finecoBlack, marginBottom: '8px', display: 'block' }}>
              {t('instrumentLists.isins')}
            </Label>
            <div
              className="custom-datasheet-wrapper custom-datasheet-wrapper-single"
              style={{ maxWidth: '100%' }}
            >
              <DataSheetGrid
                key={data.length === 1 ? 'single' : 'multiple'}
                value={data}
                onChange={handleRowChange}
                columns={columns}
                addRowsComponent={({ addRows }) => (
                  <div className="dsg-add-row">
                    <button className="dsg-add-row-btn" onClick={() => addRows(rowsToAdd)}>
                      {t('global.addRows')}
                    </button>
                    <input
                      className="dsg-add-row-input"
                      type="number"
                      min={1}
                      value={rowsToAdd}
                      onChange={(e) => setRowsToAdd(Number(e.target.value))}
                    />
                    <span style={{ marginLeft: 6 }}>{t('global.rows')}</span>
                  </div>
                )}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <FinecoButton
            click={() => {
              setListName('');
              setData([{ isin: '' }]);
              setOpen(false);
            }}
            variant="white"
            copy={t('instrumentLists.cancelList')}
            disabled={submitting}
          />
          <FinecoButton
            click={handleSubmit}
            copy={submitting ? t('actions.submitting') : t('instrumentLists.saveList')}
            disabled={isSubmitDisabled}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
